import { Helmet } from "react-helmet-async";
import { Flag, Heart, Lightbulb, Trophy, Target } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import Stats from "../components/homepage/Stats";
import PageHero, { SectionHeader, FeatureGrid } from "../components/shared/PageHero";

const VALUES = [
  { icon: Flag, title: "Ranks over reports", text: "If an app doesn't rank, a prettier dashboard doesn't help. We optimize for outcomes." },
  { icon: Heart, title: "Founder empathy", text: "Most of us shipped apps before we did ASO. We think like the founders we serve." },
  { icon: Lightbulb, title: "Ship, then learn", text: "Opinions are cheap. We test, measure, then argue with the data." },
  { icon: Trophy, title: "World-class, India-priced", text: "Jaipur cost base. San Francisco output quality. That's the whole thesis." },
  { icon: Target, title: "Own the outcome", text: "No scope-hiding, no blame loops. One team from audit to #1." },
];

const TIMELINE = [
  { year: "2022", text: "Lumotis founded in Jaipur under Lumotis Digital Media." },
  { year: "2023", text: "First 25 apps ranked. Team scales to 18. Launched UA and Retention verticals." },
  { year: "2024", text: "Expanded to UAE, USA and UK. Shipped first $1M ARR app client." },
  { year: "2025", text: "100+ apps ranked across 15+ markets. GEO / AI citation practice launched." },
];

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About Lumotis — India's #1 App Store Optimization Agency</title>
        <meta
          name="description"
          content="Lumotis is a premium app growth agency built in Jaipur, India. Founded in 2022 under Lumotis Digital Media — 100+ apps ranked across 15+ markets."
        />
        <link rel="canonical" href="https://lumotis.com/about" />
      </Helmet>
      <Navbar />
      <main data-testid="about-page">
        <PageHero
          testid="about-hero"
          eyebrow="About"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "About" }]}
          title="Built in Jaipur. Ranking apps from"
          titleAccent="California to Singapore."
          subtitle="Lumotis is a premium app growth agency founded in 2022 under the parent company Lumotis Digital Media. We're a team of engineers, marketers and designers obsessed with one question: why isn't your app #1 yet?"
          primaryCta={{ to: "/contact", label: "Work with us", testid: "about-cta-primary" }}
          secondaryCta={{ to: "/careers", label: "Join the team", testid: "about-cta-secondary" }}
        />

        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <SectionHeader
              eyebrow="01 — Values"
              title="Five things we believe, deeply."
              description="No fluff. These are the rules we hire, fire and argue by."
            />
            <FeatureGrid items={VALUES.slice(0, 6)} testidPrefix="about-value" />
          </div>
        </section>

        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <SectionHeader
              eyebrow="02 — Timeline"
              title="From Jaipur garage to 15+ markets."
            />
            <ol className="border-l border-zinc-200 ml-4 space-y-10">
              {TIMELINE.map((t, idx) => (
                <li key={t.year} className="pl-8 relative" data-testid={`timeline-${idx}`}>
                  <span className="absolute -left-[7px] top-1 w-3.5 h-3.5 bg-electric border-2 border-white shadow" />
                  <p className="font-display text-3xl font-black text-charcoal">
                    {t.year}
                  </p>
                  <p className="mt-2 text-zinc-600 leading-relaxed max-w-xl">
                    {t.text}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <Stats />
        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
